import React from "react";
import { Task, Project, TaskStatus } from "../../types/Work";

interface TaskCardProps {
  task: Task;
  projects?: Project[];
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onToggleTimer: (task: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
}

const TASK_STATUS_ORDER: TaskStatus[] = [
  "backlog", "todo", "in_progress", "in_review", "done",
];

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  projects,
  onEdit,
  onDelete,
  onToggleTimer,
  onStatusChange,
}) => {
  const [showMoveOptions, setShowMoveOptions] = React.useState(false);
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "#ff3b30";
      case "high":
        return "#ff9500";
      case "medium":
        return "#ffcc00";
      case "low":
        return "#8e8e93";
      default:
        return "#8e8e93";
    }
  };

  const getPriorityDots = (priority: string) => {
    const levels = { low: 1, medium: 2, high: 3, urgent: 4 };
    const level = levels[priority as keyof typeof levels] || 1;
    const color = getPriorityColor(priority);

    return Array.from({ length: 4 }, (_, i) => (
      <span
        key={i}
        className="dot"
        style={{
          backgroundColor: color,
          opacity: i < level ? 1 : 0.3,
        }}
      ></span>
    ));
  };

  const getDescriptionExcerpt = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.length > 120 ? text.substring(0, 120) + "..." : text;
  };

  const getTimeSinceCreation = () => {
    const now = new Date();
    const created = new Date(task.createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    }
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  };

  const getDeadlineText = () => {
    if (!task.deadline) return null;

    const now = new Date();
    const deadline = new Date(task.deadline);
    const diffMs = deadline.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) {
      return { text: "Overdue", urgent: true };
    }
    if (diffDays > 0) {
      return {
        text: `Due in ${diffDays} day${diffDays > 1 ? "s" : ""}`,
        urgent: diffDays <= 1,
      };
    }
    return {
      text: `Due in ${diffHours} hour${diffHours > 1 ? "s" : ""}`,
      urgent: true,
    };
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  const getTodayTimeSpent = () => {
    if (!task.timeEntries || task.timeEntries.length === 0) return 0;

    return task.timeEntries.reduce((total, entry) => {
      const entryStart = new Date(entry.startAt);
      if (entryStart >= todayStart && entryStart < tomorrowStart) {
        return total + (entry.durationMinutes || 0);
      }
      return total;
    }, 0);
  };

  const formatTimeSpent = (minutes: number) => {
    if (minutes === 0) return null;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  };

  const completedSubtasks = task.subtasks?.filter((s) => s.done).length || 0;
  const totalSubtasks = task.subtasks?.length || 0;
  const subtaskProgress =
    totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0;

  const project = projects?.find((p) => p.id === task.projectId);
  const projectInitials = project?.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
  const deadlineInfo = getDeadlineText();
  const todayTimeSpent = getTodayTimeSpent();
  const todayTimeFormated = formatTimeSpent(todayTimeSpent);
  const workedToday =
    (task.timeEntries || []).some((entry) => {
      const start = new Date(entry.startAt).getTime();
      const end = entry.endAt
        ? new Date(entry.endAt).getTime()
        : start + (entry.durationMinutes || 0) * 60_000;
      return end > todayStart.getTime() && start < tomorrowStart.getTime();
    }) ||
    (task.isTimerRunning && task.currentTimerStart
      ? new Date(task.currentTimerStart).getTime() < tomorrowStart.getTime() &&
        Date.now() >= todayStart.getTime()
      : false);
  const hasMoveOptions = task.status !== "in_progress";
  const statusIndex = TASK_STATUS_ORDER.indexOf(task.status);
  const previousStatus = TASK_STATUS_ORDER[statusIndex - 1];
  const nextStatus = TASK_STATUS_ORDER[statusIndex + 1];

  const moveTo = (status: TaskStatus) => {
    setShowMoveOptions(false);
    onStatusChange(task.id, status);
  };

  return (
    <div
      className={`task-card p-0 ${task.isTimerRunning ? "timer-active" : ""} ${workedToday ? "worked-today" : ""}`}
      onClick={() => onEdit(task)}
    >
      {task.isTimerRunning && (
        <div className="timer-indicator">
          <i className="fas fa-circle"></i>
          <span>Timer running</span>
        </div>
      )}
      <div className="task-card-content">
        <div className="task-title-row">
          <h4 className="task-title">{task.name}</h4>
          {project && (
            <span
              className="task-project-initials"
              style={{ backgroundColor: project.color }}
              title={project.name}
              aria-label={`Project: ${project.name}`}
            >
              {projectInitials}
            </span>
          )}
          {task.status === "in_progress" && (
            <button
              type="button"
              className={`task-timer-shortcut ${task.isTimerRunning ? "is-running" : ""}`}
              title={task.isTimerRunning ? "Stop timer" : "Start timer"}
              aria-label={
                task.isTimerRunning
                  ? `Stop timer for ${task.name}`
                  : `Start timer for ${task.name}`
              }
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onToggleTimer(task);
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <i
                className={`fas ${task.isTimerRunning ? "fa-stop" : "fa-play"}`}
              ></i>
            </button>
          )}
          {hasMoveOptions && (
            <button
              type="button"
              className="task-timer-shortcut task-more-shortcut"
              title="Move task"
              aria-label={`Move options for ${task.name}`}
              aria-expanded={showMoveOptions}
              onClick={(event) => {
                event.stopPropagation();
                setShowMoveOptions((value) => !value);
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <i className="fas fa-ellipsis"></i>
            </button>
          )}
        </div>

        {hasMoveOptions && showMoveOptions && (
          <div
            className="task-move-overlay"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="task-move-close"
              aria-label="Close move options"
              onClick={() => setShowMoveOptions(false)}
            >
              <i className="fas fa-xmark"></i>
            </button>
            <span className="task-move-heading">Move task</span>
            <div className="task-move-actions">
              <button
                type="button"
                disabled={!previousStatus}
                onClick={() => previousStatus && moveTo(previousStatus)}
              >
                <i className="fas fa-arrow-left"></i>
                Previous
              </button>
              <button
                type="button"
                disabled={!nextStatus}
                onClick={() => nextStatus && moveTo(nextStatus)}
              >
                <i className="fas fa-arrow-right"></i>
                Next
              </button>
              <button type="button" disabled={task.status === "done"} onClick={() => moveTo("done")}>
                <i className="fas fa-forward-fast"></i>
                Done
              </button>
            </div>
          </div>
        )}

        {task.descriptionHtml && (
          <p className="task-description-excerpt">
            {getDescriptionExcerpt(task.descriptionHtml)}
          </p>
        )}

        {/* Priority and Progress Row */}
        <div className="task-priority-progress-row">
          {/* Priority Dots */}
          <div className="task-priority-dots">
            <div className="dots-container">
              {getPriorityDots(task.priority)}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="task-progress-section">
            {totalSubtasks > 0 ? (
              <>
                <div className="progress-header">
                  {/* <span className="progress-label">Progress</span> */}
                  {/* <span className="progress-text">
                    {completedSubtasks}/{totalSubtasks}
                  </span> */}
                </div>
                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${subtaskProgress}%`,
                      backgroundColor: "rgb(48 206 212)",
                    }}
                  ></div>
                </div>
              </>
            ) : (
              <div className="progress-header">
                <span className="progress-label">No subtasks</span>
              </div>
            )}
          </div>
        </div>

        <div className="task-footer">
          <div className="task-meta-item">
            <i className="fas fa-clock"></i>
            <span>{getTimeSinceCreation()}</span>
          </div>
          {task.estimationMinutes && (
            <div className="task-meta-item estimation">
              <i className="fas fa-stopwatch"></i>
              <span>{formatTimeSpent(task.estimationMinutes)} est.</span>
            </div>
          )}
          {todayTimeFormated && (
            <div className="task-meta-item time-spent-today">
              <i className="fas fa-hourglass-half"></i>
              <span>{todayTimeFormated} today</span>
            </div>
          )}
          {deadlineInfo && (
            <div
              className={`task-meta-item deadline ${deadlineInfo.urgent ? "urgent" : ""}`}
            >
              <i className="fas fa-flag"></i>
              <span>{deadlineInfo.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
